import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { MODAL_TYPES } from '../../common/constants'
import i18n from '../../i18n'
import { hideModal, showModal } from '../../store/modal'
import { resetTableCheckbox } from '../../store/table'
import { updateToast } from '../helpers'
import { createOffer, getOfferTemplates, previewOffer } from './functions'
import { OfferCreateResponse, OfferEntityType, OfferPayload, OfferPreviewPayload, OfferPreviewResponse, OfferTemplatesResponse } from './types'

const offers = {
    CreateOffer: {
        useMutation: (options?: UseMutationOptions<OfferCreateResponse, AxiosError, OfferPayload>) => {
            const dispatch = useDispatch()
            const { OFFER_SEND_MODAL, OFFER_BASE_MODAL } = MODAL_TYPES

            return useMutation({
                onMutate: () => {
                    const toastId = toast.loading(i18n.t('toast.loading.offer') as string)
                    return toastId
                },
                mutationFn: (payload: OfferPayload) => createOffer(payload),
                onSuccess: (_data, _variables, context) => {
                    const toastId = context as number
                    toastId && updateToast(toastId, i18n.t('toast.loading.offer.success') as string, 'success')

                    dispatch(hideModal({ modalType: OFFER_SEND_MODAL }))
                    dispatch(hideModal({ modalType: OFFER_BASE_MODAL }))
                    dispatch(resetTableCheckbox(true))
                },
                onError: (_error, _variables, context) => {
                    const toastId = context as number
                    toastId && updateToast(toastId, i18n.t('toast.loading.offer.error') as string, 'error')
                },
                ...options,
            })
        },
    },
    PreviewOffer: {
        useMutation: (options?: UseMutationOptions<OfferPreviewResponse, AxiosError, OfferPreviewPayload>) => {
            const dispatch = useDispatch()

            return useMutation({
                onMutate: () => {
                    const toastId = toast.loading(i18n.t('toast.offer.preview.loading') as string)
                    return toastId
                },
                mutationFn: (payload: OfferPreviewPayload) => previewOffer(payload),
                onSuccess: (data, variables, context) => {
                    const toastId = context as number
                    toast.dismiss(toastId)

                    const offerTemplate = variables.data.offer_template
                    const offerType = variables.data.offer_type
                    const offerLocale = variables.data.offer_locale
                    const agentId = variables.data.agent_id

                    const modalPayload = {
                        modalType: MODAL_TYPES.OFFER_PREVIEW_MODAL,
                        modalProps: {
                            agentId,
                            offerTemplate,
                            offerType,
                            offerLocale,
                            previewCount: variables.previewCount,
                            offerSettingsGroup: variables.data.offer_settings_group,
                            pdfUrl: data.pdfUrl,
                        },
                    }

                    dispatch(showModal(modalPayload))
                },
                onError: (_error, _variables, context) => {
                    const toastId = context as number

                    toast.dismiss(toastId)
                    toast.error(i18n.t('toast.offer.preview.error') as string)
                },
                ...options,
            })
        },
    },
    GetOfferTemplates: {
        useQuery: (entityType: OfferEntityType, options?: UseQueryOptions<OfferTemplatesResponse, AxiosError>) =>
            useQuery({
                queryKey: ['offerTemplates', entityType],
                queryFn: () => getOfferTemplates(entityType),
                staleTime: Infinity,
                gcTime: Infinity,
                ...options,
            }),
    },
}

export default offers
